import { z } from 'zod'

/**
 * Esquema v1 del documento musical (§3). Las claves van en inglés; la interfaz, en español.
 *
 * Jerarquía: canción → arreglo → revisiones inmutables. Lo único mutable de un arreglo es el
 * puntero a su revisión principal. La cejilla y las tonalidades viven en la revisión porque
 * cambiarlas crea una revisión nueva (ADR 0003).
 */
export const SCHEMA_VERSION = 1 as const

export const IdSchema = z.string().regex(/^[A-Za-z0-9_-]{1,64}$/, 'ID no válido')

export const NotationSchema = z.enum(['american', 'latin'])

export const SectionKindSchema = z.enum([
  'intro',
  'verse',
  'prechorus',
  'chorus',
  'bridge',
  'instrumental',
  'solo',
  'interlude',
  'outro',
  'tab',
  'other',
])

/** De dónde sale un dato: fuente, estimación de la IA, ajuste tuyo, importación o regla. */
export const ProvenanceSchema = z.object({
  kind: z.enum(['source', 'ai', 'user', 'import', 'rule', 'sync']),
  /** Nombre o URL de la fuente. */
  source: z.string().max(500).optional(),
  /** Cómo se obtuvo: «chordpro», «rule:4-bars-per-line», modelo de IA… */
  method: z.string().max(200).optional(),
  confidence: z.number().min(0).max(1).optional(),
  at: z.iso.datetime().optional(),
  /** «Por revisar»: dudoso hasta que lo aceptes. */
  needsReview: z.boolean().optional(),
})

export const ChordAnchorSchema = z.object({
  id: IdSchema,
  /** Posición en el texto NFC de la línea, en unidades UTF-16. Puede ser igual a la longitud. */
  at: z.number().int().nonnegative(),
  /** Acorde tal como se escribió («Rem», «D/F#»). */
  chord: z.string().min(1).max(40),
})

export const LineSchema = z.object({
  id: IdSchema,
  /** Ausente equivale a «lyrics». Un comentario es una indicación («Repetir x2»). */
  kind: z.enum(['lyrics', 'comment']).optional(),
  text: z.string().max(2000),
  chords: z.array(ChordAnchorSchema).max(200),
})

export const SectionSchema = z.object({
  id: IdSchema,
  kind: SectionKindSchema,
  label: z.string().max(100).optional(),
  lines: z.array(LineSchema).max(500),
})

/** Cada aparición de una sección en la interpretación tiene su propio ID. */
export const OccurrenceSchema = z.object({
  id: IdSchema,
  section: IdSchema,
})

const BeatsSchema = z.number().positive().max(256)

/**
 * Duración tal como se marcó al grabar los cambios, sin ajustar a la rejilla (§5.4). Se conserva
 * al cuantizar para poder volver a ajustarla con otra rejilla. Añadido en E2: opcional, así que
 * las fichas anteriores siguen siendo válidas sin migrarlas (ADR 0007).
 */
const TappedSchema = z.number().positive().max(256)

export const BeatEventSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('chord'),
    occurrence: IdSchema,
    chord: IdSchema,
    beats: BeatsSchema,
    tapped: TappedSchema.optional(),
  }),
  z.object({
    kind: z.literal('rest'),
    occurrence: IdSchema,
    beats: BeatsSchema,
    tapped: TappedSchema.optional(),
  }),
])

export const BeatMapSchema = z.object({
  status: z.enum(['absent', 'estimated', 'reviewed']),
  origin: z.string().max(200).optional(),
  events: z.array(BeatEventSchema).max(10000),
})

export const TimeSignatureSchema = z
  .string()
  .regex(/^([1-9]|1[0-6])\/(1|2|4|8|16)$/, 'Compás no válido')

/** Figura que recibe un pulso. En 6/8 se cuenta normalmente a negra con puntillo («3/8»). */
export const BeatUnitSchema = z.enum(['1/2', '1/4', '3/8', '1/8'])

export const BpmSchema = z.number().min(20).max(320)

export const RevisionContentSchema = z.object({
  /** Notación en la que están escritos los acordes. La pantalla puede mostrar otra. */
  notation: NotationSchema,
  /** Ausente equivale a afinación estándar (E A D G B E). */
  tuning: z.string().max(40).optional(),
  capo: z.number().int().min(0).max(12),
  /** Tonalidad de las formas que tocas (con cejilla, la de los dedos). */
  shapeKey: z.string().max(12).optional(),
  /** Tonalidad que suena. */
  soundingKey: z.string().max(12).optional(),
  timeSignature: TimeSignatureSchema.optional(),
  beatUnit: BeatUnitSchema.optional(),
  /** Solo el tempo de referencia. El de práctica es estado de práctica, no de la revisión. */
  tempo: z.object({ reference: z.object({ bpm: BpmSchema }).optional() }),
  sections: z.array(SectionSchema).max(200),
  sequence: z.array(OccurrenceSchema).max(500),
  beatMap: BeatMapSchema,
  /** Procedencia por ruta de dato: «tempo.reference», «shapeKey», «beatMap»… */
  provenance: z.record(z.string(), ProvenanceSchema),
  /** Otros metadatos de la ficha (álbum, año, compositor…). */
  meta: z.record(z.string(), z.string().max(500)).optional(),
  notes: z.string().max(10000).optional(),
})

export const RevisionSchema = z.object({
  id: IdSchema,
  arrangementId: IdSchema,
  parentRevisionId: IdSchema.nullable(),
  author: z.enum(['user', 'ai', 'import', 'sync']),
  createdAt: z.iso.datetime(),
  reason: z.string().max(500).optional(),
  content: RevisionContentSchema,
})

export const ArrangementSchema = z.object({
  id: IdSchema,
  songId: IdSchema,
  name: z.string().min(1).max(100),
  mainRevisionId: IdSchema,
  createdAt: z.iso.datetime(),
})

export const ReferenceRecordingSchema = z.object({
  id: IdSchema,
  kind: z.enum(['studio', 'live', 'other']),
  title: z.string().max(200).optional(),
  url: z.url().optional(),
  note: z.string().max(500).optional(),
})

export const SourceLinkSchema = z.object({
  url: z.url(),
  label: z.string().max(200).optional(),
  /** Condiciones de uso de la fuente, si se conocen. */
  terms: z.string().max(500).optional(),
})

export const SongSchema = z.object({
  id: IdSchema,
  title: z.string().min(1).max(200),
  artist: z.string().max(200),
  externalIds: z.record(z.string(), z.string().max(200)).optional(),
  references: z.array(ReferenceRecordingSchema).max(20).optional(),
  links: z.array(SourceLinkSchema).max(20).optional(),
  createdAt: z.iso.datetime(),
})

/** Una ficha completa: la unidad de importación y exportación. */
export const SongDocumentSchema = z.object({
  schemaVersion: z.literal(SCHEMA_VERSION),
  song: SongSchema,
  arrangement: ArrangementSchema,
  revision: RevisionSchema,
})

export type Id = z.infer<typeof IdSchema>
export type SectionKind = z.infer<typeof SectionKindSchema>
export type Provenance = z.infer<typeof ProvenanceSchema>
export type ChordAnchor = z.infer<typeof ChordAnchorSchema>
export type Line = z.infer<typeof LineSchema>
export type Section = z.infer<typeof SectionSchema>
export type Occurrence = z.infer<typeof OccurrenceSchema>
export type BeatEvent = z.infer<typeof BeatEventSchema>
export type BeatMap = z.infer<typeof BeatMapSchema>
export type RevisionContent = z.infer<typeof RevisionContentSchema>
export type Revision = z.infer<typeof RevisionSchema>
export type Arrangement = z.infer<typeof ArrangementSchema>
export type Song = z.infer<typeof SongSchema>
export type SongDocument = z.infer<typeof SongDocumentSchema>
